import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { SectionCard } from '../ui/Detail';
import { Button } from '../ui/Button';
import { Sheet } from '../ui/Sheet';
import { WarrantyBadge, AgeLabel } from './WarrantyBadge';
import { EquipmentForm } from './EquipmentForm';
import { field } from '../create/Field';
import { IconPlus } from '../icons';

type Row = {
  equipment: {
    id: string;
    name: string;
    make: string | null;
    model: string | null;
    serial: string | null;
    installedOn: string | null;
    warrantyExpiresOn: string | null;
    laborWarrantyExpiresOn: string | null;
    active: boolean;
  };
  propertyLabel: string;
  propertyId: string;
};

/**
 * What this customer owns (PRD v2 §6.7) — grouped by address, because a commercial
 * customer with nine rooftop units across three buildings is exactly who this is for.
 */
export function CustomerEquipmentCard({ customerId }: { customerId: string }) {
  const trpc = useTRPC();
  const q = useQuery(trpc.equipment.forCustomer.queryOptions({ customerId }));
  const properties = useQuery(trpc.customer.properties.queryOptions({ customerId }));
  const [adding, setAdding] = useState(false);

  const rows = (q.data ?? []) as Row[];
  const byProperty = new Map<string, Row[]>();
  for (const r of rows) {
    byProperty.set(r.propertyLabel, [...(byProperty.get(r.propertyLabel) ?? []), r]);
  }

  return (
    <SectionCard
      title={rows.length === 0 ? 'Equipment' : `Equipment (${rows.length})`}
      right={
        (properties.data ?? []).length > 0 ? (
          <Button variant="ghost" onClick={() => setAdding(true)}>
            <IconPlus size={14} />
            Add a unit
          </Button>
        ) : undefined
      }
    >
      {q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <p className="text-sm text-[var(--sw-danger-text)]">
          Couldn't load their equipment.{' '}
          <button type="button" className="underline" onClick={() => void q.refetch()}>
            Try again
          </button>
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing recorded yet. Add the units you service and every job on them builds a
          history worth quoting from.
        </p>
      ) : (
        <div className="space-y-3">
          {[...byProperty.entries()].map(([label, units]) => (
            <div key={label}>
              {byProperty.size > 1 ? (
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {label}
                </p>
              ) : null}
              <ul className="divide-y divide-border text-sm">
                {units.map(({ equipment: e }) => (
                  <li
                    key={e.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2"
                  >
                    <Link
                      to="/equipment/$equipmentId"
                      params={{ equipmentId: e.id }}
                      className="min-w-0 flex-1 truncate font-medium hover:underline"
                    >
                      {e.name}
                      {!e.active ? (
                        <span className="text-muted-foreground"> · retired</span>
                      ) : null}
                    </Link>
                    <WarrantyBadge
                      partsExpiresOn={e.warrantyExpiresOn}
                      laborExpiresOn={e.laborWarrantyExpiresOn}
                    />
                    <p className="w-full text-xs text-muted-foreground">
                      {[e.make, e.model].filter(Boolean).join(' ') ||
                        'Make/model not recorded'}
                      {e.serial ? ` · #${e.serial}` : ''} ·{' '}
                      <AgeLabel installedOn={e.installedOn} />
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <AddUnitSheet
          properties={(properties.data ?? []).map((p) => ({ id: p.id, label: p.label }))}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </SectionCard>
  );
}

function AddUnitSheet({
  properties,
  onClose,
}: {
  properties: { id: string; label: string }[];
  onClose: () => void;
}) {
  const [propertyId, setPropertyId] = useState(properties[0]?.id ?? '');

  return (
    <Sheet open onClose={onClose} title="Add a unit">
      <div className="space-y-3">
        {/* Only asked when there's a real choice — most customers have one address. */}
        {properties.length > 1 ? (
          <label className="block text-xs font-medium">
            <span className="mb-1 block text-muted-foreground">Which property</span>
            <select
              className={field}
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
            >
              {properties.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <EquipmentForm propertyId={propertyId} onDone={onClose} onCancel={onClose} />
      </div>
    </Sheet>
  );
}
